import React, { useEffect, useState, useRef, useCallback, useContext, useId } from 'react'
import * as Zeego from 'zeego/dropdown-menu'
import usePlatform from '@/utils/hooks/usePlatform';
import { ActivityIndicator, Pressable, ScrollView, Text, View } from 'react-native';
import Animated, { Easing, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import SearchBox from './inputs/SearchBox';
import { LegendList } from '@legendapp/list';
import { withUnistyles, StyleSheet, useUnistyles } from 'react-native-unistyles';
import useDropdownStore from '@/data/store/useDropdownStore';
import { useModalPortalContainer } from '@/data/store/useModalPortalStore';
import {Tooltip} from './Tooltip';
import Unicon from './Unicon';
import { usePressBlockerStore } from '@/data/store/usePressBlockerStore';
import GuideTarget from '@/components/Guides/GuideTarget';
import { GuideContext } from '@/components/Guides/GuideProvider';
import SheetModal from './SheetModal';

const ZeegoItemStyledWeb = withUnistyles(Zeego.Item);
const ZeegoItemStyledNative = Zeego.Item;

const DropdownMenu = ({title, multiple, options, testID, dropdownComponent, triggerComponent, containerStyle, triggerStyle, showFocusOutline = false, align = 'start', dropdownWidth, showSearch = false, forceToolbar = false, toolBarButtons = [], isLoading = false, onSearch = null, onOpen = null, onClose, shouldStopPropagation = true, open: controlledOpen, onOpenChange: controlledOnOpenChange, disabled = false, guideTargetID = null}) => {
    const { isNative, isWeb } = usePlatform();
    const guide = useContext(GuideContext);
    const notifyActivation = guide?.onTargetActivated;

    const globalSetOpen = useDropdownStore((state) => state.setOpen)
    // Portal dropdown content into the enclosing web modal (if any) so it
    // renders above the modal overlay. The container comes from
    // ModalPortalContext — each modal (ModalWrapper/SheetModal/AssistantScreen)
    // provides its own portal-safe host node to its subtree, which keeps
    // stacked modals correct at any depth: a lower modal's dropdown renders
    // below the upper one instead of portaling into it. Outside any modal the
    // hook falls back to the top-of-stack registered node (see
    // useModalPortalStore for why registration replaced the old
    // querySelectorAll lookup, which picked up Reanimated exit-lingering modal
    // nodes and broke all dropdowns after navigating away from a modal).
    const modalPortalContainer = useModalPortalContainer();
    const [searchText, setSearchText] = useState("")
    const [internalOpen, setInternalOpen] = useState(false);
    const [programmaticOpen, setProgrammaticOpen] = useState(false);
    const [nativeMenuOpen, setNativeMenuOpen] = useState(false);
    const menuID = useId();

    const isControlled = controlledOpen !== undefined;
    const open = isControlled ? controlledOpen : internalOpen;

    const prevControlledOpenRef = useRef();

    const { blockNextPress } = usePressBlockerStore.getState();

    const { theme: { colors, fonts } } = useUnistyles();
    const firstItemRef = useRef(null);
    const searchRef = useRef(null);
    const listContainerRef = useRef(null);
    // One-shot flag armed by multi-select item picks: zeego's Item wrapper
    // calls our onSelect WITHOUT the Radix select event, so preventDefault
    // can't run inside it (the old shouldDismissMenuOnSelect prop did this
    // inside zeego but leaked onto the DOM). The menu's open state is
    // controlled, so the close request Radix fires right after the select can
    // be ignored safely in Zeego.Root's onOpenChange. A setTimeout disarm
    // covers selects that never produce a close request, so a stale flag can
    // never swallow a real close (the bug the previous skipCloseRef had).
    const suppressNextCloseRef = useRef(false);
    // Set by the info button's pointerdown so the enclosing Zeego item's
    // onSelect knows to ignore the press (info click must not select the item
    // or close the menu).
    const infoActionRef = useRef(false);

    const animation = useSharedValue(0);

    const animatedStyle = useAnimatedStyle(() => {
        return {
            opacity: animation.value,
        };
    });

    const visibleOpen = isWeb || programmaticOpen ? open : nativeMenuOpen;
    useEffect(() => {
        globalSetOpen?.(visibleOpen, menuID);
        return () => globalSetOpen?.(false, menuID);
    }, [visibleOpen, globalSetOpen, menuID]);

    const handleOpenChange = useCallback((willOpen, fromEffect = false) => {
        setSearchText("")
        if(onSearch) onSearch("")

        if (willOpen) {
            if (guideTargetID) notifyActivation?.(guideTargetID);
            setInternalOpen(true);
            if(onOpen) onOpen();

            animation.value = withTiming(1, { duration: 150, easing: Easing.inOut(Easing.ease) });

            setTimeout(focusSearch, 200);
        } else {
            animation.value = withTiming(0, { duration: 150, easing: Easing.inOut(Easing.ease) });

            setTimeout(() => {
                setInternalOpen(false);
                if(onClose) onClose();
            }, 150);
        }
        if (isControlled && controlledOnOpenChange && !fromEffect) {
            controlledOnOpenChange(willOpen);
        }
    }, [isControlled, controlledOnOpenChange, onOpen, onClose, onSearch, searchRef.current, notifyActivation, guideTargetID])

    useEffect(() => {
        if (isControlled && controlledOpen !== prevControlledOpenRef.current) {
            if (controlledOpen && !internalOpen) {
                handleOpenChange(true, true);
            } else if (!controlledOpen && internalOpen) {
                handleOpenChange(false, true);
            }
            prevControlledOpenRef.current = controlledOpen;
        }
    }, [isControlled, controlledOpen, internalOpen, handleOpenChange]);

    const listContainerCallbackRef = useCallback((node) => {
        listContainerRef.current = node;
        if (!isWeb || !showSearch || !node) return;
        const el = node instanceof HTMLElement ? node : node?.getNode?.() ?? node;
        if (!el?.addEventListener) return;
        const stopProp = (e) => e.stopPropagation();
        const preventDef = (e) => e.preventDefault();
        const refocusSearch = () => {
            if (searchRef.current && document.activeElement !== searchRef.current) {
                searchRef.current.focus();
            }
        };
        el.addEventListener('pointermove', stopProp, true);
        el.addEventListener('pointerenter', refocusSearch, true);
        el.addEventListener('pointerleave', refocusSearch, true);
        el.addEventListener('pointerdown', preventDef, true);
    }, [isWeb, showSearch]);

    const focusSearch = () => {
        if(isWeb && searchRef.current) {
            searchRef.current?.focus();
        }
    }

    const ZeegoItemStyled = isWeb ? ZeegoItemStyledWeb : ZeegoItemStyledNative;

    const trigger = triggerComponent || <View style={styles.triggerIcon}><Unicon name="ellipsis-horizontal" size={22} colorVariant="text" /></View>
    const stopTriggerPropagation = (event) => {
        if(shouldStopPropagation) event?.stopPropagation?.();
    };

    const modalParent = isWeb ? modalPortalContainer : null;

    const renderItem = useCallback(({item: option, index}) => {
        if (option.separator) {
            return <View key={`separator-${index}`} style={styles.separator} />;
        }
        // Section headers — non-selectable group labels (e.g. {section: 'Forms'}).
        if (option.section) {
            return isNative
                ? <Zeego.Item key={`section-${index}`} disabled><Zeego.ItemTitle style={{ color: colors.textSecondary, fontFamily: fonts.bold }}>{option.section}</Zeego.ItemTitle></Zeego.Item>
                : <View key={`section-${index}`} style={styles.sectionHeader}><Text style={styles.sectionHeaderText}>{option.section}</Text></View>;
        }
        return <ZeegoItemStyled
            key={index}
            disabled={option.disabled}
            onSelect={(e) => {
                // Info button was pressed — ignore the item selection entirely
                // and keep the menu open.
                if (infoActionRef.current) {
                    infoActionRef.current = false;
                    e?.preventDefault?.();
                    return;
                }
                if(shouldStopPropagation) blockNextPress();
                // Multi-select on web must stay open after a pick. Zeego's
                // Item calls onSelect without the Radix event (see
                // suppressNextCloseRef above), so arm the one-shot flag — the
                // close request Radix fires next gets ignored in the root
                // onOpenChange and the menu stays open.
                if (isWeb) {
                    // `dismissOnSelect` opts a single item out of multi-select
                    // persistence — Radix closes the menu naturally and its
                    // onOpenChange drives our close path once.
                    if (multiple && !option.dismissOnSelect) {
                        suppressNextCloseRef.current = true;
                        setTimeout(() => { suppressNextCloseRef.current = false; }, 0);
                    }
                    else if (!multiple) handleOpenChange(false);
                }

                option.onSelect(option);
            }}
            destructive={isWeb ? (option.destructive ? option.destructive.toString() : 'false') : option.destructive} 
            style={styles.item(option.selected, option.disabled)}
            ref={index == 0 ? firstItemRef : null}
        >
            <Zeego.ItemIcon
                androidIconName={option.icon?.android}
                ios={{
                    name: option.icon?.ios,
                    pointSize: 16,
                    weight: 'regular',
                    scale: 'medium',
                }}
            >
                {option.icon?.avatar 
                    ? <View style={styles.avatarWrapper}>{option.icon.avatar}</View> 
                    : <View style={styles.iconWrapper(option.icon?.web)}><Unicon name={option.icon?.web} library={option.icon?.library || 'ionicon'} size={17} colorVariant={option.destructive ? "error" : "text"}  /></View>}
            </Zeego.ItemIcon>

            {isWeb ?
                <View style={{flex: 1, justifyContent:'space-between', flexDirection:'row', alignItems:'center'}}>
                    {multiple && !option.hideCheckbox && (
                        <View style={styles.checkboxLeft}>
                            <Unicon name={option.selected ? 'checkbox' : 'square-outline'} size={18} colorVariant={option.selected ? 'success' : 'textSecondary'} />
                        </View>
                    )}
                    <View style={styles.webContentWrapper}>
                        <Zeego.ItemTitle style={{color: option.destructive ? colors.error : colors.text, fontFamily: fonts.body, fontSize: 14}}>{option.name}</Zeego.ItemTitle>
                        <Zeego.ItemSubtitle style={{color: colors.textSecondary, fontFamily: fonts.heading, fontSize: 12}}>{option.subtitle}</Zeego.ItemSubtitle>
                        {option.body ? <Text style={styles.itemBody(option.isError)}>{option.body}</Text> : null}
                    </View>
                    <View style={styles.itemRightActions}>
                        {option.infoAction && (
                            <Pressable
                                testID={`Dropdown-ItemInfo-${option.id}`}
                                onPointerDown={(e) => { e.stopPropagation(); infoActionRef.current = true; }}
                                onPointerUp={(e) => { e.stopPropagation(); }}
                                onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
                                onPress={(e) => { e?.stopPropagation?.(); e?.preventDefault?.(); option.infoAction(); }}
                                style={styles.infoButton}
                            >
                                <Unicon name="information-circle-outline" size={18} colorVariant="textSecondary" />
                            </Pressable>
                        )}
                        {!multiple && option.selected && <Unicon name='checkmark' size={18} colorVariant="primary" />}
                    </View>
                </View>
            :
                <>
                    <Zeego.ItemTitle>{option.name}</Zeego.ItemTitle>
                    <Zeego.ItemSubtitle>{option.subtitle}</Zeego.ItemSubtitle>
                </>
            }
        </ZeegoItemStyled>
    }, [multiple, colors, fonts, isWeb, shouldStopPropagation, blockNextPress, handleOpenChange, firstItemRef, ZeegoItemStyled, infoActionRef])

    // When searching, drop separators/section headers — they'd orphan without
    // their matching items and read as clutter.
    const visibleOptions = (searchText && !onSearch)
        ? options?.filter(option => !option.separator && !option.section && option.name?.toLowerCase().includes(searchText.toLowerCase()))
        : options;
    const optionComponents = visibleOptions?.map((option, index) => renderItem({item: option, index}))

    const toolbar = forceToolbar || toolBarButtons?.filter(t => t.visible).length > 0 ? (
        <View style={styles.toolbar}>
            <Text style={styles.toolbarTitle}>{title}</Text>
            <View style={styles.toolbarActions}>
                {toolBarButtons?.filter(t => t.visible).map((button, index) => (
                    <Tooltip key={index} onPress={() => {
                        button.onPress()
                        if (!button.keepOpen) handleOpenChange(false)
                    }} style={styles.toolbarButton} testID={`Dropdown-Toolbar-${button.name}`} title={button.tooltip}>
                        <Unicon name={button.icon?.name} library={button.icon?.library} size={button.icon?.size ?? 20} colorVariant="text" />
                    </Tooltip>
                ))}

                <View style={styles.toolbarActionSeparator}></View>

                <Tooltip style={styles.toolbarButton} onPress={() => handleOpenChange(false)} testID="Dropdown-Toolbar-close" title="Close">
                    <Unicon name="close" size={20} colorVariant="text" />
                </Tooltip>
            </View>
        </View>
    ) : <></>

    const Target = guideTargetID ? GuideTarget : View;
    return <Target style={containerStyle} {...(guideTargetID ? { id: guideTargetID, activate: disabled ? undefined : () => {
        if (isNative) setProgrammaticOpen(true);
        handleOpenChange(true);
    } } : {})}>
    <View
        testID={"DropdownMenu-" + (testID ?? title ?? "")}
        onPointerDown={stopTriggerPropagation}
        onTouchEnd={stopTriggerPropagation}
        onClick={stopTriggerPropagation}
    ><Zeego.Root open={open} onOpenChange={willOpen => {
        if (isNative) setNativeMenuOpen(willOpen);
        // Swallow the close request that follows a multi-select pick (armed
        // in the item's onSelect above) — the menu must stay open.
        if (!willOpen && suppressNextCloseRef.current) {
            suppressNextCloseRef.current = false;
            return;
        }
        handleOpenChange(willOpen);
    }}>

        <Zeego.Trigger disabled={disabled} asChild={isWeb} style={{backgroundColor: 'transparent', border:0, cursor: 'pointer'}}>
            <Pressable disabled={disabled} hitSlop={15} testID={"DropdownTrigger-" + (testID ?? title ?? "")} style={triggerStyle ? [styles.trigger(showFocusOutline), triggerStyle] : styles.trigger(showFocusOutline)}>{trigger}</Pressable>
        </Zeego.Trigger>

        <Zeego.Content align={align} style={{zIndex:10000}} container={modalParent ?? null}>
            {isNative 
                ? (title ? [
                        <Zeego.Item key="title" disabled><Zeego.ItemTitle>{title}</Zeego.ItemTitle></Zeego.Item>,
                        ...optionComponents
                    ] : optionComponents)
                : (
                    <Animated.View style={[styles.dropdownContainer(dropdownWidth), animatedStyle]}>
                        {dropdownComponent 
                            ? dropdownComponent(() => handleOpenChange(false))
                            : <>
                                {toolbar}

                                {showSearch && 
                                    <View style={styles.searchContainer}>
                                        {(onSearch && isLoading) && <View style={styles.loadingContainer}><ActivityIndicator size="small" color={colors.primary} /></View>}
                                        <SearchBox ref={searchRef} focusRefOnArrowKey={firstItemRef} textChangeDelay={onSearch ? 400 : 0} nobackground searchText={searchText} onChangeText={(new_value) => {
                                            setSearchText(new_value)
                                            if(onSearch) onSearch(new_value)
                                        }} />
                                    </View>
                                }

                                <View style={styles.listContainer} ref={listContainerCallbackRef}>
                                    {isWeb && options.length <= 200 ? (
                                        <ScrollView keyboardShouldPersistTaps="handled">
                                            {optionComponents}
                                            {optionComponents?.length === 0 && !isLoading && (
                                                <View style={styles.emptyListContainer}>
                                                    <Text style={styles.emptyListText}>No results found</Text>
                                                </View>
                                            )}
                                        </ScrollView>
                                    ) : (
                                        <LegendList
                                            showsVerticalScrollIndicator={false}
                                            keyExtractor={(item) => item.id ?? item.name}
                                            estimatedItemSize={38}
                                            data={visibleOptions}
                                            progressViewOffset={1000}
                                            refreshing={false}
                                            renderItem={renderItem}
                                            ListEmptyComponent={(!isLoading && optionComponents?.length == 0) && <View style={styles.emptyListContainer}><Text style={styles.emptyListText}>No results found</Text></View>}
                                            keyboardShouldPersistTaps="handled"
                                        />
                                    )}
                                </View>
                            </>
                        }
                    </Animated.View>
                )}
        </Zeego.Content>
    </Zeego.Root></View>
    {isNative && guideTargetID && <SheetModal
        isOpen={programmaticOpen && open}
        title={title || 'Choose an option'}
        onClose={() => handleOpenChange(false)}
        onDismissed={() => setProgrammaticOpen(false)}
    >
        {options?.map((option, index) => option.separator ? <View key={`separator-${index}`} style={styles.separator} /> : option.section ? <View key={`section-${index}`} style={styles.sectionHeader}><Text style={styles.sectionHeaderText}>{option.section}</Text></View> : (
            <Pressable key={option.id ?? option.name ?? index} testID={`DropdownOption-${testID ?? guideTargetID}-${index}`} disabled={option.disabled} style={[styles.item(option.selected, option.disabled), { flexDirection: 'row', minHeight: 44 }]} onPress={() => {
                option.onSelect(option);
                if (!multiple) handleOpenChange(false);
            }}>
                <View style={styles.webContentWrapper}>
                    <Text style={{ color: option.destructive ? colors.error : colors.text, fontFamily: fonts.body, fontSize: 14 }}>{option.name}</Text>
                    {!!option.subtitle && <Text style={{ color: colors.textSecondary, fontFamily: fonts.body, fontSize: 12 }}>{option.subtitle}</Text>}
                </View>
                {!!option.selected && <Unicon name="checkmark" size={18} colorVariant="primary" />}
            </Pressable>
        ))}
    </SheetModal>}
    </Target>
}


const styles = StyleSheet.create(({colors, fonts}, rt) => {
    return {
        item: (selected, disabled) => ({
            padding:10, 
            display:"flex", 
            alignItems:"center", 
            gap:10, 
            cursor:"pointer", 
            backgroundColor: selected ? colors.bgPrimary : colors.surface, 
            outline: "none",
            opacity: disabled ? 0.5 : 1,
            _web: {
                _hover: {
                    backgroundColor: disabled ? colors.surface : (selected ? colors.bgPrimary : colors.hover),
                },
                _focus: {
                    backgroundColor: disabled ? colors.surface : colors.hover,
                }
            }
        }),
        trigger: (showFocusOutline) => ({
            cursor:"pointer",
            borderRadius:showFocusOutline ? 8 : 0,
            outlineStyle: 'none',
            _web:{
                _focus: {
                    boxShadow: showFocusOutline ? `${colors.primary} 0px 0px 3px 0px` : "none",
                }
            }
        }),
        iconWrapper: (hasWebIcon) => ({
            alignItems:"center",
            justifyContent:"center",
            width: hasWebIcon ? 20 : 0, 
            marginTop: -3
        }),
        avatarWrapper: {
            width: 26,
            borderRadius: 14,
            overflow: 'hidden',
        },
        webContentWrapper: {
            flex:1,
            gap:2
        },
        checkboxLeft: {
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: 10,
        },
        itemRightActions: {
            flexDirection: 'row',
            alignItems: 'center',
            gap: 6,
        },
        infoButton: {
            padding: 2,
            borderRadius: 4,
            _web: {
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                _hover: {
                    backgroundColor: colors.hover,
                },
            },
        },
        itemBody: (isError) => ({
            color: isError ? colors.error : colors.textSecondary, 
            fontFamily: fonts.body, 
            fontSize: 12, 
            fontStyle: 'italic'
        }),
        toolbar: {
            paddingHorizontal:10, 
            paddingVertical:7, 
            flexDirection:"row", 
            alignItems:"center", 
            justifyContent:"space-between", 
            gap:5, 
            borderBottomWidth:2, 
            borderBottomColor: colors.border
        },
        toolbarTitle: {
            color:colors.textSecondary, 
            fontSize:12,  
            fontFamily: fonts.bold
        },
        toolbarActions: {
            flexDirection:"row", 
            gap:5
        },
        toolbarActionSeparator: {
            width:1,
            height:26,
            backgroundColor:colors.border
        },
        toolbarButton: {
            borderRadius:5,
            padding:3,
            _web: {
                transition:"all 0.2s ease-in-out",
                cursor:"pointer",
                _hover: {
                    backgroundColor: colors.hover,
                }
            }
        },
        dropdownContainer: (dropdownWidth) => ({
            marginTop:10, 
            backgroundColor: colors.surface, 
            borderRadius: 8, 
            boxShadow: "rgba(0, 0, 0, 0.4) 0px 0px 15px -5px", 
            borderWidth: 1, 
            borderColor: colors.border, 
            minWidth:220,
            // maxHeight:(rt.screen.height/2) - 50,
            width:dropdownWidth || 'auto', 
            overflow: 'hidden'
        }),
        searchContainer: {
            borderBottomWidth:1, 
            borderBottomColor: colors.border
        },
        loadingContainer: {
            padding:10, 
            position:"absolute", 
            top:-2, 
            right:0
        },
        listContainer: {
            maxHeight: (rt.screen.height/2) - 125
        },
        emptyListContainer: {
            padding:10
        },
        emptyListText: {
            color:colors.textSecondary, 
            fontSize:12, 
            textAlign:"center", 
            fontStyle:"italic"
        },
        triggerIcon: {
            paddingHorizontal:5,
            borderRadius:5,
            transition:"all 0.1s ease-in-out",
            _web: {
                _hover: {
                    backgroundColor: colors.hover,
                }
            }
        },
        separator: {
            height: 1,
            backgroundColor: colors.border,
        },
        sectionHeader: {
            paddingHorizontal: 14,
            paddingTop: 10,
            paddingBottom: 4,
        },
        sectionHeaderText: {
            color: colors.textTertiary,
            fontFamily: fonts.bold,
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: 0.6,
        }
    }
})


const dropdownIcons = {
    edit: {
        android: "edit",
        ios: "pencil",
        web: "pencil"
    },
    delete: {
        android: "delete",
        ios: "trash",
        web: "trash"
    },
    create: {
        android: "create",
        ios: "plus",
        web: "add"
    },
    settings: {
        android: "settings",
        ios: "gear",
        web: "settings-outline"
    },
    pin:{
        android: "push_pin",
        ios: "pin.fill",
        web: "pin",
        library: "material"
    },
    unpin:{
        android: "push_pin",
        ios: "pin.slash.fill",
        web: "pin-off",
        library: "material"
    },
    hide:{
        android: "visibility_off",
        ios: "eye-slash",
        web: "eye-off"
    },
    unhide:{
        android: "visibility",
        ios: "eye",
        web: "eye"
    },
    export:{
        android: "file_download",
        ios: "square.and.arrow.down",
        web: "download-outline"
    },
    help:{
        android: "help",
        ios: "questionmark.circle",
        web: "help-circle-outline"
    },
    add:{
        android: "person_add",
        ios: "person.badge.plus",
        web: "person-add"
    },
    barcode:{
        android: "qr_code_scanner",
        ios: "barcode.viewfinder",
        web: "barcode"
    },
    lock:{
        android: "lock",
        ios: "lock.fill",
        web: "lock-closed"
    },
    unlock:{
        android: "lock_open",
        ios: "lock.open.fill",
        web: "lock-open"
    },
    print:{
        android: "print",
        ios: "printer.fill",
        web: "print"
    },
    archive:{
        android: "archive",
        ios: "archivebox",
        web: "archive-outline"
    },
    unarchive:{
        android: "unarchive",
        ios: "arrow.uturn.up.circle",
        web: "arrow-undo-outline"
    },
    favorite:{
        android: "favorite",
        ios: "heart.fill",
        web: "heart"
    },
    favoriteRemove:{
        android: "favorite_border",
        ios: "heart.slash.fill",
        web: "heart-dislike"
    },
    calendar:{
        android: "calendar",
        ios: "calendar",
        web: "calendar"
    },
    people:{
        android: "people",
        ios: "person.2",
        web: "people"
    },
    createFolder:{
        android: "create_new_folder",
        ios: "folder.fill.badge.plus",
        web: "folder-plus",
        library: "material"
    },
    copy:{
        android: "content_copy",
        ios: "doc.on.doc",
        web: "copy"
    },
    info:{
        android: "info_outline",
        ios: "info.circle",
        web: "information-circle-outline"
    },
    megaphone:{
        android: "campaign",
        ios: "megaphone",
        web: "megaphone-outline"
    }
}

export default DropdownMenu
export {dropdownIcons}